/* =========================================================
   EAZY CLOTHES — KOLO ER
   Jeden modul obslugujacy dwa tryby tej samej sekcji:

   >= 820px  TARCZA — obrot po okregu, wzorzec tablist, panele
             pokazywane pojedynczo. Zachowanie bez zmian.
   <  820px  KARUZELA — ery przewijane w bok, wszystkie panele widoczne
             w torze scroll-snap, pasek lat jako nawigacja skrotowa.

   Dlaczego jeden modul, a nie dwa: oba tryby operuja na tych samych
   panelach i tych samych przyciskach lat. Dwa niezalezne moduly
   walczylyby o atrybut hidden i o role ARIA.

   Przewijanie w trybie mobilnym jest w calosci natywne (CSS scroll-snap).
   Skrypt niczego nie przechwytuje — tylko slucha, ktora karta jest
   na srodku, i odswieza pasek lat oraz licznik.

   Bez localStorage/sessionStorage.
   ========================================================= */

(function ezEraDial() {
  const ROOT_SELECTOR = '[data-era-root]';
  const DESKTOP_QUERY = '(min-width: 820px)';

  /* Promien rozmieszczenia etykiet, w procentach polowy szerokosci kola.
     44 zamiast 50, zeby etykiety siedzialy na obwodzie, a nie poza nim. */
  const LABEL_RADIUS = 44;
  const ROTATION_DURATION = 600;
  const SWAP_DELAY = 140;

  /* Po tylu ms sprawdzamy, czy plynne przewijanie w ogole ruszylo. */
  const SMOOTH_FALLBACK = 120;

  /* Awaryjne domkniecie przewijania programowego. Zdarzenie scrollend nie
     jest dostepne na starszych iOS, wiec ten timeout NIE jest opcjonalny —
     bez niego flaga zostalaby podniesiona na zawsze i observer przestalby
     aktualizowac stan po pierwszym dotknieciu roku. */
  const SETTLE_FALLBACK = 700;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Odpowiednik cubic-bezier(0.16, 1, 0.3, 1) — te sama krzywa co --ease w CSS. */
  const easeOutExpo = (t) => 1 - Math.pow(1 - t, 3);

  const initEra = (root) => {
    if (root.dataset.eraReady === 'true') return;

    const track = root.querySelector('[data-era-track]');
    const labels = Array.from(root.querySelectorAll('[data-era-label]'));
    const tabs = Array.from(root.querySelectorAll('[data-era-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-era-panel]'));
    const centerYear = root.querySelector('[data-era-center-year]');
    const centerName = root.querySelector('[data-era-center-name]');
    const centerYears = root.querySelector('[data-era-center-years]');
    const progressBar = root.querySelector('[data-era-progress-bar]');
    const progressCurrent = root.querySelector('[data-era-progress-current]');

    const count = tabs.length;
    if (count === 0) return;

    const stepAngle = 360 / count;
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);

    let mode = null;
    let currentIndex = 0;
    let currentAngle = 0;
    let isAnimating = false;
    let swapTimer = null;
    let observer = null;

    /* Wlasciciel stanu w trybie karuzeli.
       Wczesniej currentIndex pisaly trzy miejsca naraz — klikniecie roku
       (z gory, przed przewinieciem), observer i przelacznik trybow — bez
       ustalenia, ktore z nich w danym momencie prowadzi. Gdy przewiniecie
       ladowalo gdzie indziej niz zaklada klikniecie, stan i widok
       rozjezdzaly sie na stale.

       Teraz zasada jest jedna: dopoki trwa przewijanie programowe,
       observer nie pisze do stanu. Po zatrzymaniu stan jest odczytywany
       z faktycznej pozycji przewiniecia i w razie potrzeby korygowany. */
    let programmatic = false;
    let settleTimer = null;

    /* --- wspolne dla obu trybow --- */

    const markActiveYear = (index) => {
      tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });

      if (progressCurrent) progressCurrent.textContent = String(index + 1);
      if (progressBar) {
        const share = 100 / count;
        progressBar.style.width = `${share}%`;
        progressBar.style.left = `${index * share}%`;
      }
    };

    /* --- TRYB TARCZY (desktop) --- */

    const positionLabels = (rotationDeg) => {
      labels.forEach((label, i) => {
        const angleDeg = -90 + i * stepAngle + rotationDeg;
        const angleRad = (angleDeg * Math.PI) / 180;
        // Procenty, nie piksele — kolo skaluje sie z viewportem.
        label.style.left = `${50 + LABEL_RADIUS * Math.cos(angleRad)}%`;
        label.style.top = `${50 + LABEL_RADIUS * Math.sin(angleRad)}%`;
      });
    };

    const applyDialSelection = (index) => {
      labels.forEach((l, i) => l.classList.toggle('is-active', i === index));

      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      panels.forEach((panel, i) => {
        if (i === index) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      markActiveYear(index);

      const activeTab = tabs[index];
      if (centerYear) centerYear.textContent = activeTab.dataset.eraYear || '';
      if (centerName) centerName.textContent = activeTab.dataset.eraName || '';
      // Zakres lat maja tylko niektore ery — pusty ciag zwija element.
      if (centerYears) centerYears.textContent = activeTab.dataset.eraYears || '';
    };

    const rotateTo = (targetAngle, onDone) => {
      const startAngle = currentAngle;
      let startTime = null;

      const frame = (timestamp) => {
        if (startTime === null) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / ROTATION_DURATION, 1);
        positionLabels(startAngle + (targetAngle - startAngle) * easeOutExpo(progress));

        if (progress < 1) {
          window.requestAnimationFrame(frame);
        } else {
          currentAngle = targetAngle;
          onDone();
        }
      };

      window.requestAnimationFrame(frame);
    };

    const setEraDial = (index, { focusTab = false } = {}) => {
      if (index === currentIndex || isAnimating) return;

      /* Najkrotsza droga po okregu: roznica sprowadzona do zakresu
         (-count/2, count/2], zeby kolo nie obracalo sie dookola. */
      let diff = index - currentIndex;
      const half = count / 2;
      if (diff > half) diff -= count;
      if (diff < -half) diff += count;

      const targetAngle = currentAngle - diff * stepAngle;

      window.clearTimeout(swapTimer);

      if (prefersReducedMotion()) {
        currentAngle = targetAngle;
        positionLabels(currentAngle);
        currentIndex = index;
        applyDialSelection(index);
        if (focusTab) tabs[index].focus();
        return;
      }

      isAnimating = true;
      root.classList.add('is-swapping');

      swapTimer = window.setTimeout(() => {
        currentIndex = index;
        applyDialSelection(index);
        root.classList.remove('is-swapping');
        if (focusTab) tabs[index].focus();
      }, SWAP_DELAY);

      rotateTo(targetAngle, () => {
        isAnimating = false;
      });
    };

    /* --- TRYB KARUZELI (mobile) --- */

    /* Ktory panel jest realnie najblizej srodka toru. Zrodlo prawdy
       po zatrzymaniu przewijania — niezaleznie od tego, co zakladalo
       klikniecie. */
    const nearestIndexFromScroll = () => {
      if (!track) return currentIndex;
      const center = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDistance = Infinity;
      panels.forEach((panel, i) => {
        const panelCenter = panel.offsetLeft + panel.offsetWidth / 2;
        const distance = Math.abs(panelCenter - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    };

    const endProgrammatic = () => {
      window.clearTimeout(settleTimer);
      if (!programmatic) return;
      programmatic = false;

      const actual = nearestIndexFromScroll();
      if (actual !== currentIndex) {
        currentIndex = actual;
        applyCarouselSelection(actual);
      }
    };

    const beginProgrammatic = () => {
      programmatic = true;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(endProgrammatic, SETTLE_FALLBACK);
    };

    const applyCarouselSelection = (index) => {
      markActiveYear(index);
      tabs.forEach((tab, i) => {
        if (i === index) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    };

    /* Docelowa pozycja przewiniecia, zeby panel wyladowal na srodku toru.
       Liczona z offsetLeft, bo scrollIntoView z behavior 'smooth' na
       kontenerze scroll-snap potrafi nie ruszyc go wcale — zmierzone:
       scrollLeft zostawal na 0 przez cale 900 ms, podczas gdy ten sam
       skok z behavior 'auto' dzialal natychmiast. */
    const centerScrollFor = (index) => {
      const panel = panels[index];
      if (!panel || !track) return null;
      const raw = panel.offsetLeft + panel.offsetWidth / 2 - track.clientWidth / 2;
      const max = track.scrollWidth - track.clientWidth;
      return Math.max(0, Math.min(raw, max));
    };

    const scrollToIndex = (index) => {
      const target = centerScrollFor(index);
      if (target === null) return;

      if (prefersReducedMotion()) {
        track.scrollLeft = target;
        return;
      }

      const before = track.scrollLeft;
      track.scrollTo({ left: target, behavior: 'smooth' });

      /* Zabezpieczenie na silniki, ktore plynne przewijanie na kontenerze
         snap ignoruja: jesli po chwili nic sie nie ruszylo, skaczemy. */
      window.setTimeout(() => {
        const stuck = Math.abs(track.scrollLeft - before) < 1;
        const notThere = Math.abs(track.scrollLeft - target) > 1;
        if (stuck && notThere) track.scrollLeft = target;
      }, SMOOTH_FALLBACK);
    };

    const setEraCarousel = (index, { focusTab = false } = {}) => {
      if (!panels[index]) return;

      /* Pasek reaguje od razu, zeby dotkniecie roku bylo wyczuwalne.
         Stan zostanie potwierdzony po zatrzymaniu przewijania. */
      currentIndex = index;
      applyCarouselSelection(index);

      beginProgrammatic();
      scrollToIndex(index);

      if (focusTab) tabs[index].focus();
    };

    const startObserver = () => {
      if (typeof IntersectionObserver === 'undefined' || !track) return;

      observer = new IntersectionObserver(
        (entries) => {
          /* Podczas przewijania programowego prowadzi klikniecie —
             observer widzi wtedy karty mijane po drodze i gdyby pisal
             do stanu, pasek migalby przez posrednie lata i konczyl
             na zlym. */
          if (programmatic) return;

          /* Karta zajmuje 86% toru, wiec w danym momencie tylko jedna
             przekracza polowe widocznosci — bierzemy te najlepiej widoczna. */
          let best = null;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (best === null || entry.intersectionRatio > best.intersectionRatio) {
              best = entry;
            }
          });
          if (best === null) return;

          const index = panels.indexOf(best.target);
          if (index === -1 || index === currentIndex) return;

          currentIndex = index;
          applyCarouselSelection(index);
        },
        { root: track, threshold: [0.5, 0.75, 1] }
      );

      panels.forEach((panel) => observer.observe(panel));

      /* scrollend domyka przewijanie natychmiast tam, gdzie jest wspierane.
         Tam, gdzie go nie ma (starsze iOS), robi to SETTLE_FALLBACK. */
      if (track) track.addEventListener('scrollend', endProgrammatic);
    };

    const stopObserver = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (track) track.removeEventListener('scrollend', endProgrammatic);
      window.clearTimeout(settleTimer);
      programmatic = false;
    };

    /* --- przelaczanie trybow --- */

    const enterDesktop = () => {
      stopObserver();

      root.querySelector('.ez-era__nav')?.setAttribute('role', 'tablist');
      tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panels[i] ? panels[i].id : '');
        tab.removeAttribute('aria-current');
      });
      panels.forEach((panel, i) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabs[i] ? tabs[i].id : '');
      });

      /* Kat trzeba przeliczyc od nowa: w karuzeli uzytkownik mogl przewinac
         do innej ery, a tarcza nadal stalaby w pozycji sprzed przelaczenia.
         Etykieta o indeksie i siedzi pod katem -90 + i*step + rotacja,
         wiec zeby aktywna trafila na godzine 12, rotacja = -i*step. */
      currentAngle = -currentIndex * stepAngle;

      positionLabels(currentAngle);
      applyDialSelection(currentIndex);
    };

    const enterMobile = () => {
      /* Semantyka zakladek nie opisuje karuzeli: panele nie sa ukrywane,
         tylko przewijane. Zdejmujemy role tab/tabpanel, a aktywny rok
         oznaczamy przez aria-current, ktore pasuje do nawigacji. */
      root.querySelector('.ez-era__nav')?.removeAttribute('role');
      tabs.forEach((tab) => {
        tab.removeAttribute('role');
        tab.removeAttribute('aria-selected');
        tab.removeAttribute('aria-controls');
      });
      panels.forEach((panel) => {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('hidden');
      });

      markActiveYear(currentIndex);
      tabs.forEach((tab, i) => {
        if (i === currentIndex) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      startObserver();
    };

    const applyMode = () => {
      const next = desktopQuery.matches ? 'desktop' : 'mobile';
      if (next === mode) return;
      mode = next;

      window.clearTimeout(swapTimer);
      root.classList.remove('is-swapping');
      isAnimating = false;

      if (mode === 'desktop') {
        enterDesktop();
      } else {
        enterMobile();
      }
    };

    const goTo = (index, options) => {
      if (mode === 'desktop') {
        setEraDial(index, options);
      } else {
        setEraCarousel(index, options);
      }
    };

    /* --- zdarzenia --- */

    labels.forEach((label, i) => {
      label.addEventListener('click', () => goTo(i));
    });

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => goTo(i));

      tab.addEventListener('keydown', (event) => {
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (i + 1) % count;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (i - 1 + count) % count;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = count - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        goTo(next, { focusTab: true });
      });
    });

    desktopQuery.addEventListener('change', applyMode);
    applyMode();

    root.dataset.eraReady = 'true';
  };

  const initAll = (scope) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initEra);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  /* W edytorze motywu bloki mozna dodawac i usuwac na zywo. */
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
